import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import CancelIcon from '@mui/icons-material/Cancel';
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from "react-toastify";
import { commonFileUpload, getAllNofificationForDashboard } from "src/thunks/dashboard";
import toastMessages from "src/constant/toastMessages.json";
import { addClientFormValidationSchema } from "src/helpers/yup_schema";
import { addClient, editClient } from "src/thunks/clientProjects";
import { GetError } from "src/constant";

export const AddClientForm = ({ setOpen, setIsEditTextShown, isEditTextShown, fetchData, editData }) => {

    /** redux store useDispatch & useSelector ------------------------------ **/
    const dispatch = useDispatch();
    const { addNewClientLoading } = useSelector((state) => state.clientProjects);

    /** hooks useState ----------------------------------------------------- **/
    const [profileObject, setProfileObject] = useState({ src: editData?.logoUrl || '' });
    const [isLoading, setIsLoading] = useState(false);

    /** react hook form declaration ------------------------------ **/
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(addClientFormValidationSchema),
    });

    /** hooks useEffect----------------------------------------------------- **/

    useEffect(() => {
        return () => {
            setIsEditTextShown(false);
        }
    }, [])

    /** useEffect for to reset the data in the form, which is required for editing ---------------------- **/
    useEffect(() => {
        if (Object.keys(editData).length > 0) {
            reset({
                clientName: editData?.name || '',
                street: editData?.address?.street1 || '',
                town: editData?.address?.city || '',
                country: editData?.address?.country || '',
                postcode: editData?.address?.zip || '',
                firstName: editData?.contactPerson?.split(" ")[0] || '',
                middleName: editData?.contactPerson?.split(" ")[1] || '',
                lastName: editData?.contactPerson?.split(" ")[2] || '',
                email: editData?.email || '',
                phone: editData?.phoneNumber || '',
            })
        }
    }, [editData])


    /** handler functions--------------------------------------------------- **/

    /** Function to handle photo uploads and save them into the state ----------------------------------- **/
    const photoUpload = async (e) => {
        setIsLoading(true);
        e.preventDefault();
        const reader = new FileReader();
        const file = e.target.files[0];
        if (file) {
            const fileType = file.type;
            if (!["image/png", "image/jpg", "image/jpeg", "image/jfif", "image/PNG", "image/JPG", "image/JPEG"].includes(fileType)) {
                setIsLoading(false);
                toast.error("Please select valid image format");
            } else {
                const formData = new FormData();
                formData.append("files", file)
                reader.onloadend = async () => {
                    let res = await dispatch(commonFileUpload(formData))
                    setIsLoading(false);
                    if (res) {
                        const urlData = res.payload[0].url;
                        setProfileObject({ ...profileObject, src: urlData });
                    }
                };
                reader.readAsDataURL(file);
            }
        }
    };

    /** Function executes when adding a new client or editing an existing client ---------------------------------- **/
    const onAddClientSubmitHandler = async (data) => {
        let newData =
        {
            name: data.clientName,
            details: data.clientName,
            contactPerson: data?.firstName + " " + data?.middleName + " " + data?.lastName,
            email: data.email,
            phoneNumber: (data.phone).toString(),
            address: {
                street1: data.street,
                country: data.country,
                city: data.town,
                zip: data.postcode,
            },
            logoUrl: profileObject.src
        }
        if (editData?.isActive) {
            newData = {
                ...newData,
                isActive: editData?.isActive || false
            }
        }
        try {
            let result = await dispatch(
                Object.keys(editData).length > 0 ?
                    editClient({ ...newData, id: editData?.id }) :
                    addClient(newData)).unwrap();
            fetchData()
            if (result) {
                setTimeout(() => {
                    dispatch(getAllNofificationForDashboard(1, 20));
                }, 2000);
                if (Object.keys(editData).length > 0) {
                    toast.success(toastMessages.clientEdited);
                } else {
                    toast.success(toastMessages.clientAdded);
                }
                handleCancelButtonClick(false);
                setProfileObject({ src: "" })
                reset();
            }
        } catch (err) {
            toast.error(GetError(err?.response?.data?.errors) || "something issue");

        }

    };

    /** Function to handle the click event for cancelling. ---------------------------- **/
    const handleCancelButtonClick = () => {
        setOpen(false);
        reset();
        setProfileObject({ src: "" });
    };

    return (
        <>
            <div>
                <div className="depot-form-header-style">{isEditTextShown ? "EDIT CLIENT" : "ADD CLIENT"}</div>
                <div style={{ marginTop: 20 }}>
                    <form className="add-depotpersonal-form-mainclass"
                        style={{ paddingLeft: 100, paddingRight: 100 }}
                        onSubmit={handleSubmit(onAddClientSubmitHandler)}
                    >
                        <div className="d-flex flex-row justify-content-between align-items-center">
                            <div className="profileUpload-for-client">
                                {!profileObject.src ? <span className="upload-logo-for-client-text-style">UPLOAD LOGO</span> :
                                    <img className="file-input-upload profile-img" src={profileObject.src} />}
                                <input className="file-input upload" type="file" accept='.png, .jpg, .jpeg, .PNG, .JPG, .JPEG' onChange={(e) => photoUpload(e)} />
                            </div>
                        </div>

                        <div className="loader-overlay" style={{ display: isLoading ? 'flex' : 'none' }}>
                            <CircularProgress />
                        </div>

                        <div className="row mt-2">
                            <div className="col-md-8">
                                <TextField className="depot-form-textfields-style"  {...register("clientName")} id="standard-basic" label="CLIENT NAME" variant="standard" />
                                <div className="error-field-class">
                                    {errors.clientName?.message && errors.clientName?.message}
                                </div>
                            </div>
                            <div className="col-md-4">
                            </div>
                        </div>

                        <span className="section-name-form-style">ADDRESS</span>
                        <div className="row m-0">
                            <TextField className="depot-form-textfields-style"  {...register("street")} id="standard-basic" label="STREET" variant="standard" />
                            <div className="error-field-class depotform-street-class">
                                {errors.street?.message && errors.street?.message}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4">
                                <TextField className="depot-form-textfields-style"  {...register("town")} id="standard-basic" label="TOWN" variant="standard" />
                                <div className="error-field-class">
                                    {errors.town?.message && errors.town?.message}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <TextField className="depot-form-textfields-style"  {...register("country")} id="standard-basic" label="COUNTRY" variant="standard" />
                                <div className="error-field-class">
                                    {errors.country?.message && errors.country?.message}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <TextField className="depot-form-textfields-style"  {...register("postcode")} id="standard-basic" label="POSTCODE" variant="standard" />
                                <div className="error-field-class">
                                    {errors.postcode?.message && errors.postcode?.message}
                                </div>
                            </div>
                        </div>

                        <span className="section-name-form-style">CONTACT PERSON</span>
                        <div className="row mt-2">
                            <div className="col-md-4">
                                <TextField className="depot-form-textfields-style"   {...register("firstName")} id="standard-basic" label="FIRST NAME" variant="standard" />
                                <div className="error-field-class">
                                    {errors.firstName?.message && errors.firstName?.message}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <TextField className="depot-form-textfields-style"  {...register("middleName")} id="standard-basic" label="MIDDLE NAME(OPTIONAL)" variant="standard" />
                            </div>
                            <div className=" col-md-4">
                                <TextField className="depot-form-textfields-style"  {...register("lastName")} id="standard-basic" label="LAST NAME" variant="standard" />
                                <div className="error-field-class">
                                    {errors.lastName?.message && errors.lastName?.message}
                                </div>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <TextField className="depot-form-textfields-style"  {...register("email")} id="standard-basic" label="EMAIL" variant="standard" />
                                <div className="error-field-class">
                                    {errors.email?.message && errors.email?.message}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <TextField type="string" className="depot-form-textfields-style"  {...register("phone")} id="standard-basic" label="PHONE" variant="standard" />
                                <div className="error-field-class">
                                    {errors.phone?.message && errors.phone?.message}
                                </div>
                            </div>
                            <div className="form-floating col-md-4">
                            </div>
                        </div>
                        <div
                            className="d-flex flex-row justify-content-between"
                            style={{ marginBottom: 20 }}
                        >
                            <Button variant="outlined" className="btn-bootstrap-submit-style" style={{ color: "red", borderColor: "red" }} startIcon={<CancelIcon />} onClick={() => handleCancelButtonClick()}>
                                Cancel
                            </Button>
                            {isEditTextShown ?
                                <Button variant="outlined" className="btn-bootstrap-submit-style" startIcon={<CheckCircleIcon />} type="submit"
                                >
                                    Save
                                </Button>
                                :
                                <Button
                                    disabled={addNewClientLoading ? true : false}
                                    variant="outlined"
                                    className={`btn-bootstrap-submit-style ${addNewClientLoading ? "fade-out" : ""}`}
                                    startIcon={<CheckCircleIcon />} type="submit"
                                >
                                    Submit
                                </Button>}
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}